import React, { useEffect } from 'react';
import PropertyCard from '@src/v3/components/propertyCard';
import PaginationComponent from '@src/v3/components/coreUI/pagination';

import PropertyListShimmer from '@src/v3/components/propertyListShimmer';
import {
  DeleteUnsaveRenterSyncPayload,
  PostSaveRenterSyncPayload,
  Property,
} from '@src/v3/interfaces/app/propertyLists';
import {
  getUserId,
  getUserType,
  isLoggedIn,
  removeSavedPropertyId,
  setSavedPropertyId,
} from '@src/v3/utils/localStorage';
import { ZEME_USERS_ROLE } from '@src/v3/constants/configs';
import usePropertyListing from '@src/v3/hooks/usePropertyListing';
import {
  deleteRenterunSaveSync,
  postRenterSaveSync,
} from '@src/v3/services/properties';
import { PropertyListingProps } from '@src/v3/interfaces/components/property/propertyListing';

const PropertyListing = ({
  role = '',
  propertyList = [],
  isLoading,
  currentPage = 1,
  totalPages = 0,
  onPageChange = () => {
    return;
  },
}: PropertyListingProps) => {
  const { setPropertyList } = usePropertyListing();

  /**
   * This method is used for save renter property
   */
  const renterSaveProperty = async (propertyId: string, userId: string) => {
    try {
      const payload: PostSaveRenterSyncPayload = {
        renter_id: userId,
        property_ids: [propertyId],
      };
      const { saves = [] } = await postRenterSaveSync(payload);

      if (saves.length) {
        const updatedPropertyList: Property[] = propertyList.map(
          (property: Property) =>
            property.id === propertyId
              ? { ...property, isSave: !property.isSave }
              : { ...property }
        );
        setPropertyList(updatedPropertyList);
      }
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * This method is used for unsave renter property
   */
  const renterUnsaveProperty = async (propertyId: string) => {
    try {
      const payload: DeleteUnsaveRenterSyncPayload = {
        property_id: propertyId,
      };
      const { saves = [] } = await deleteRenterunSaveSync(payload);

      if (saves.length) {
        const updatedPropertyList: Property[] = propertyList.map(
          (property: Property) =>
            property.id === propertyId
              ? { ...property, isSave: !property.isSave }
              : { ...property }
        );
        setPropertyList(updatedPropertyList);
      }
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * This method is used for saveunsave button event
   */
  const handleSaveUnsaveProperty = (propertyId: string, isSave: boolean) => {
    if (!isLoggedIn()) {
      setSavedPropertyId(propertyId);
      const updatedPropertyList: Property[] = propertyList.map(
        (property: Property) =>
          property.id === propertyId
            ? { ...property, isSave: !property.isSave }
            : { ...property }
      );
      if (isSave) {
        removeSavedPropertyId(propertyId);
      }
      setPropertyList(updatedPropertyList);
      // setCanShowLoginDialog(true);
      return;
    }

    const userType = getUserType() ?? '';
    const userId = getUserId() ?? '';
    if (userType === ZEME_USERS_ROLE.RENTER) {
      if (!isSave) {
        renterSaveProperty(propertyId, userId);
      } else {
        renterUnsaveProperty(propertyId);
      }
    }
  };

  // lifecycle hook for application
  useEffect(() => {
    return () => {
      setPropertyList([]);
    };
  }, [setPropertyList]);

  return (
    <div className="">
      {/* Skelton loader */}
      {isLoading && (
        <div className="grid grid-cols-290 gap-x-5 gap-y-5 mx-auto">
          <PropertyListShimmer />
        </div>
      )}

      {/* Listings */}
      <div className="grid grid-cols-290 gap-x-6 gap-y-8 sm:gap-y-12 mx-auto">
        {!isLoading &&
          propertyList &&
          propertyList?.map((property: Property) => {
            const image =
              property.imageInformation.images.length > 0
                ? (property.imageInformation.images[0].thumbnail as string)
                : '';

            return (
              <PropertyCard
                key={property.id}
                id={property.id}
                role={role}
                isMyListing={
                  (role === ZEME_USERS_ROLE.AGENT ||
                    role === ZEME_USERS_ROLE.LANDLORD) &&
                  property?.generalInformation?.agent?.id === getUserId()
                }
                image={image}
                name={property.generalInformation?.propertyName}
                price={property.economicInformation?.cost}
                updatedPrice={property.economicInformation?.updatedCost}
                address={property.generalInformation?.address}
                bath={property.generalInformation?.bathrooms}
                bedroom={property.generalInformation?.bedrooms}
                squareFootage={property.generalInformation?.squareFootage}
                unitCode={property.generalInformation?.address?.unitCode}
                isEasyApply={property.generalInformation?.isEasyApply || false}
                handleSaveUnsaveProperty={(id: string, isSave: boolean) =>
                  handleSaveUnsaveProperty(id, isSave)
                }
                isSaved={Boolean(property?.isSave)}
                isExclusive={property.generalInformation?.isExclusive}
                netEffectiveRent={
                  property?.economicInformation?.netEffectiveRent
                }
                leaseConcession={property?.economicInformation?.leaseConcession}
                leaseMonthsFree={String(
                  property?.economicInformation?.leaseMonthsFree
                )}
                leaseTerm={property?.economicInformation?.leaseTerm}
                brokerFee={property?.chargeInformation?.brokerFee}
              />
            );
          })}
      </div>

      {/* Pagination */}
      {!isLoading && propertyList?.length > 0 && (
        <PaginationComponent
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          maxVisiblePages={4}
        />
      )}

      {/**No result found */}
      {!isLoading && propertyList?.length == 0 && (
        <div className="mt-8">
          <h3 className="lg:text-2xl md:text-xl text-lg font-medium text-gray-1000 mb-2">
            We couldn&apos;t find any exact matches for your search
          </h3>
          <span className="font-normal text-gray-900 lg:text-xl md:text-lg text-base">
            Try changing or removing some of your filters or adjusting your
            search area
          </span>
        </div>
      )}
    </div>
  );
};

export default PropertyListing;
